import GokuDataGrid from '@/components/GokuDataGrid'
import CellLink from '@/components/GokuDataGrid/CellLink'
import { QUERY_KEYS } from '@/constants/reactQueryKeys'
import { useQueryWithHandler } from '@/hooks/useQueryWithHandler'
import { ResponseArray } from '@/types/shared'
import { paths } from '@/utils/path'
import { useMemo } from 'react'
import { useParams } from 'react-router-dom'
import { Chip, Typogaphy } from 'vnetwork-ui'
import { GridColDefVNW } from 'vnetwork-ui/dist/components/DataGridPro'
import { getItems } from '../../services'
import { Item } from '../../types/response'
import { getEquipmentStatusColor, getConditionColor } from '../../utils'

// Columns for components in equipment in this rack
const componentColumns: GridColDefVNW<Item>[] = [
    {
        field: 'name',
        headerName: 'Component Name',
        width: 250,
        renderCell: ({ row }) => (
            <CellLink to={`${paths.warehouseComponents}/${row?.id}`}>
                {row?.name}
            </CellLink>
        ),
    },
    {
        field: 'parent_item',
        headerName: 'Installed In (Equipment)',
        width: 350,
        renderCell: ({ row }) => {
            if (!row?.parent_item) {
                return (
                    <Typogaphy size="small" className="text-muted-foreground">
                        Not installed
                    </Typogaphy>
                )
            }

            const serviceTag = row.parent_item.service_tag
            const rackPosition = row.parent_item.rack_position_u
            const equipmentName = row.parent_item.name

            // Format: "DELL R640 [9GT95T2] @ U21"
            const displayText = [
                equipmentName,
                serviceTag ? `[${serviceTag}]` : null,
                rackPosition ? `@ U${rackPosition}` : null,
            ]
                .filter(Boolean)
                .join(' ')

            return (
                <CellLink
                    to={`${paths.warehouseEquipment}/${row.parent_item.id}`}
                >
                    {displayText}
                </CellLink>
            )
        },
    },
    {
        field: 'category',
        headerName: 'Category',
        width: 130,
        renderCell: ({ row }) => (
            <Typogaphy size="small" className="capitalize">
                {row?.category}
            </Typogaphy>
        ),
    },
    {
        field: 'serial_number',
        headerName: 'Serial Number',
        width: 180,
        renderCell: ({ row }) => (
            <Typogaphy size="small">
                {row?.serial_number || row?.ref || '-'}
            </Typogaphy>
        ),
    },
    {
        field: 'status',
        headerName: 'Status',
        width: 130,
        headerClassName: 'text-center',
        renderCell: ({ row }) => (
            <Chip
                label={row?.status}
                size="small"
                className="min-w-[100px] flex justify-center capitalize"
                variant="contained"
                color={getEquipmentStatusColor(row?.status)}
            />
        ),
    },
    {
        field: 'condition',
        headerName: 'Condition',
        width: 120,
        headerClassName: 'text-center',
        renderCell: ({ row }) => (
            <Chip
                label={row?.condition}
                size="small"
                className="min-w-[100px] flex justify-center capitalize"
                variant="contained"
                color={getConditionColor(row?.condition)}
            />
        ),
    },
    {
        field: 'model',
        headerName: 'Model',
        width: 200,
        renderCell: ({ row }) => (
            <Typogaphy size="small">{row?.model?.name || '-'}</Typogaphy>
        ),
    },
    {
        field: 'quantity',
        headerName: 'Qty',
        width: 80,
        headerClassName: 'text-center',
        renderCell: ({ row }) => (
            <Typogaphy size="small" className="text-center w-full">
                {row?.quantity || 1}
            </Typogaphy>
        ),
    },
]

const RackComponents = () => {
    const { id } = useParams<{ id: string }>()

    // First, get all equipment in this rack to get their IDs
    const { data: equipmentData, isLoading: isLoadingEquipment } =
        useQueryWithHandler({
            queryKey: [QUERY_KEYS.WAREHOUSE_ITEMS, 'rack-equipment', id],
            queryFn: () =>
                getItems({
                    rack_id: id,
                    item_type: 'equipment',
                    page_size: 100,
                }),
        })

    // Extract equipment IDs
    const equipmentIds = useMemo(
        () => equipmentData?.results?.map((eq: Item) => eq.id) || [],
        [equipmentData]
    )

    // Fetch all components for all equipment in this rack
    const { data: allComponentsData, isLoading: isLoadingComponents } =
        useQueryWithHandler({
            queryKey: [
                QUERY_KEYS.WAREHOUSE_ITEMS,
                'rack-all-components',
                id,
                equipmentIds,
            ],
            queryFn: async () => {
                if (equipmentIds.length === 0) {
                    return []
                }

                const allComponents: Item[] = []

                for (const equipmentId of equipmentIds) {
                    try {
                        // Fetch all pages for this equipment
                        let currentPage = 1
                        let hasMore = true

                        while (hasMore) {
                            const data = await getItems({
                                parent_id: equipmentId,
                                item_type: 'component',
                                page: currentPage,
                                page_size: 100,
                            })

                            if (data.results && data.results.length > 0) {
                                allComponents.push(...data.results)
                            }

                            const totalPages = data.pagination?.total_pages || 1
                            hasMore = currentPage < totalPages
                            currentPage++
                        }
                    } catch (error) {
                        console.error(
                            `Error fetching components for equipment ${equipmentId}:`,
                            error
                        )
                    }
                }

                return allComponents
            },
            enabled: equipmentIds.length > 0,
        })

    // Custom API call that handles client-side pagination from cached data
    const fetchComponentsInRack = async (
        params: any
    ): Promise<ResponseArray<Item>> => {
        // Use the cached components data
        const allComponents = allComponentsData || []

        // Manual pagination on client side
        const page = params.page || 1
        const pageSize = params.page_size || 10
        const startIndex = (page - 1) * pageSize
        const endIndex = startIndex + pageSize
        const paginatedComponents = allComponents.slice(startIndex, endIndex)
        const totalPages = Math.ceil(allComponents.length / pageSize)

        return {
            results: paginatedComponents,
            pagination: {
                page,
                page_size: pageSize,
                total: allComponents.length,
                total_pages: totalPages,
                page_next: page < totalPages ? page + 1 : page,
                page_prev: page > 1 ? page - 1 : page,
            },
        }
    }

    const isLoading = isLoadingEquipment || isLoadingComponents

    if (isLoading) {
        return (
            <div className="text-sm text-muted-foreground p-4">
                Loading components...
            </div>
        )
    }

    return (
        <div className="mt-2">
            {equipmentIds.length === 0 ? (
                <div className="text-sm text-muted-foreground p-4">
                    No equipment found in this rack. Add equipment first to see
                    their components.
                </div>
            ) : (
                <GokuDataGrid
                    apiCall={fetchComponentsInRack}
                    reactQueryKey={`${QUERY_KEYS.WAREHOUSE_ITEMS}-rack-components-${id}`}
                    columns={componentColumns}
                    dataGridProps={{
                        getRowId: (row) => row?.id,
                        initialState: {
                            sorting: {
                                sortModel: [
                                    { field: 'parent_item', sort: 'asc' },
                                ],
                            },
                        },
                    }}
                    actions={[]}
                    filterDefaultValues={{
                        category: '',
                        status: '',
                    }}
                />
            )}
        </div>
    )
}

export default RackComponents
